// copied and edited from packages/ui/src/components/FlavorFinder/components/Intro/FlavorFinderIntro.tsx
import type { FC } from "react";

import { useDeviceBreakpoint } from "@ui/hooks/useDeviceBreakpoint";
import { useUser } from "@ui/hooks/useUser";
import { useAuthHrefResolver } from "@ui/providers/AuthRoutingProvider";
import classNames from "classnames";
import dompurify from "isomorphic-dompurify";

import { useConfig } from "@jti/app-context/useConfig";
import { AUTH_ROUTE_PATHS } from "@jti/fe-common/routes/auth-routes";

import { FlavorFinderSkipButton, FlavorFinderStartQuizButton } from "../Buttons/FlavorFinderButtons";
import { FlavorFinderFooter, FlavorFinderFooterSticky } from "../Footer";
import { FlavorFinderHeader } from "../Header/FlavorFinderHeader";
import { useFlavorFinderContext } from "../Provider/FlavorFinderContextProvider";

import { useFillViewportHeight } from "./useFillViewportHeight";

import styles from "./FlavorFinderIntro.module.scss";

const { sanitize } = dompurify;

export const FlavorFinderIntro: FC = () => {
  const { breakpoint } = useDeviceBreakpoint();
  const { startQuiz, flavorFinderData } = useFlavorFinderContext();
  const { "base-url": baseUrl } = useConfig();
  const resolveAuthHref = useAuthHrefResolver();
  const { isUserLoggedIn } = useUser();

  const introRef = useFillViewportHeight<HTMLDivElement>();

  const {
    titleLoggedIn,
    titleNonLoggedIn,
    subtitle,
    subtitlePostscript,
    indicatorOverrideLabel,
    introBackgroundImage,
    smallIntroBackgroundImage,
    mediumIntroBackgroundImage,
    introBackgroundVideo,
    mediumIntroBackgroundVideo,
    smallIntroBackgroundVideo,
    ctaLabelStartLoggedIn,
    ctaLabelStartNonLoggedIn,
    ctaLabelLoginToStart,
  } = flavorFinderData.intro;

  const getVideoAndBackgroundImage = () => {
    switch (breakpoint) {
      case "mobile":
        return {
          backgroundImage: smallIntroBackgroundImage,
          video: smallIntroBackgroundVideo,
        };
      case "tablet":
        return {
          backgroundImage: mediumIntroBackgroundImage,
          video: mediumIntroBackgroundVideo,
        };
      case "desktop":
      default:
        return {
          backgroundImage: introBackgroundImage,
          video: introBackgroundVideo,
        };
    }
  };

  const goToLoginPage = () => {
    window.location.href = resolveAuthHref({
      route: AUTH_ROUTE_PATHS.login,
      search: { returnUrl: baseUrl },
    });
  };

  const { backgroundImage, video } = getVideoAndBackgroundImage();
  const containerStyle = video ? {} : { backgroundImage: `url('${backgroundImage}')` };

  return (
    <div ref={introRef} className={styles.intro} data-testid="flavor-finder-intro">
      <FlavorFinderHeader title={isUserLoggedIn ? titleLoggedIn : titleNonLoggedIn} infoText={indicatorOverrideLabel} />
      <div style={containerStyle} className={styles.content}>
        <div className={styles.subtitle} dangerouslySetInnerHTML={{ __html: sanitize(subtitle) }}></div>
        {Boolean(subtitlePostscript) && (
          <div
            className={styles.subtitlePostscript}
            dangerouslySetInnerHTML={{ __html: sanitize(subtitlePostscript ?? "") }}
          ></div>
        )}
        {video && (
          <video className={styles.video} src={video} autoPlay muted loop playsInline disablePictureInPicture />
        )}
      </div>
      <FlavorFinderFooter>
        <FlavorFinderFooterSticky>
          {!isUserLoggedIn && (
            <FlavorFinderSkipButton
              label={ctaLabelStartNonLoggedIn}
              onClick={startQuiz}
              primaryButton
              className={classNames(styles.button, styles.buttonSkipGuest)}
            />
          )}
          {!isUserLoggedIn && (
            <FlavorFinderStartQuizButton
              onClick={goToLoginPage}
              label={ctaLabelLoginToStart}
              className={classNames(styles.button, styles.buttonLogin)}
            />
          )}
          {isUserLoggedIn && (
            <FlavorFinderStartQuizButton label={ctaLabelStartLoggedIn} onClick={startQuiz} className={styles.button} />
          )}
        </FlavorFinderFooterSticky>
      </FlavorFinderFooter>
    </div>
  );
};
